import {Modal, Select} from 'antd';
import {ChoiceGroup, Tip} from '../shared/ui';
import {ADD, QUESTIONS} from './copy';
import {previewSample, sectionOptions} from './sourceHelpers';
import styles from '../shared/news.module.scss';

// What stops the answers being saved, in words, or null.
export const answersProblem = (answers, mustPickSection) => {
  if (mustPickSection && answers.cover !== 'section') {
    return QUESTIONS.sectionRequired;
  }
  if (answers.cover === 'section' && !answers.section) {
    return QUESTIONS.sectionRequired;
  }
  if (answers.cover === 'all' && !answers.keywords.length) {
    return QUESTIONS.keywordsNeeded;
  }
  return null;
};

// The latest articles, each marked with what the answers would do to it.
export const SamplePreview = ({samples, answers}) => (
  <div className={styles.block}>
    <h4>
      <Tip label={ADD.preview} tip={ADD.previewTip} />
    </h4>
    {samples?.length ? (
      <ul className={styles.articles}>
        {samples.map(sample => {
          const result = previewSample(sample, answers);
          return (
            <li key={sample.link}>
              <a href={sample.link} target="_blank" rel="noreferrer">
                {sample.title || sample.link}
              </a>
              {result.used ? (
                <span className={styles.used}>{ADD.willUse}</span>
              ) : (
                <span className={styles.skipped}>
                  {ADD.willSkip(result.reason)}
                </span>
              )}
            </li>
          );
        })}
      </ul>
    ) : (
      <p className={styles.muted}>{ADD.noSamples}</p>
    )}
  </div>
);

// The three questions of §4.2. `value` is {cover, keywords, section,
// reliability}; every answer sets the underlying coverage fields.
const ScopeQuestions = ({value, onChange, exchange, host, samples}) => {
  const set = changes => onChange({...value, ...changes});
  const sections = sectionOptions(samples);
  if (value.section && !sections.some(s => s.value === value.section)) {
    sections.unshift({value: value.section, count: 0});
  }

  const chooseReliability = reliability => {
    if (reliability !== 'official' || value.reliability === 'official') {
      return set({reliability});
    }
    Modal.confirm({
      title: QUESTIONS.officialConfirmTitle,
      content: QUESTIONS.officialConfirm,
      okText: 'Continue',
      onOk: () => set({reliability}),
    });
  };

  return (
    <div>
      <SamplePreview samples={samples} answers={value} />

      <h4>
        <Tip label={QUESTIONS.cover} tip={QUESTIONS.coverTip} />
      </h4>
      <ChoiceGroup
        value={value.cover}
        onChange={cover => set({cover})}
        options={[
          {value: 'market', label: QUESTIONS.coverMarket(exchange)},
          {value: 'all', label: QUESTIONS.coverAll},
          {value: 'section', label: QUESTIONS.coverSection(exchange)},
        ]}
      />

      {value.cover === 'all' ? (
        <div className={styles.block}>
          <p className={styles.questionLabel}>
            <Tip label={QUESTIONS.keywords} tip={QUESTIONS.keywordsTip} />
          </p>
          <Select
            mode="tags"
            open={false}
            className={styles.fullWidth}
            value={value.keywords}
            placeholder={QUESTIONS.keywordsAdd}
            onChange={keywords =>
              set({keywords: keywords.map(k => k.trim()).filter(Boolean)})
            }
          />
          <p className={styles.reason}>{QUESTIONS.keywordsRule}</p>
        </div>
      ) : null}

      {value.cover === 'section' ? (
        <div className={styles.block}>
          <h4>
            <Tip
              label={QUESTIONS.section}
              tip={QUESTIONS.sectionTip(exchange)}
            />
          </h4>
          {sections.length ? (
            <ChoiceGroup
              value={value.section}
              onChange={section => set({section})}
              options={sections.map(section => ({
                value: section.value,
                label: QUESTIONS.sectionOption(
                  host,
                  section.value,
                  section.count,
                ),
              }))}
            />
          ) : (
            <p className={styles.muted}>{QUESTIONS.sectionNone}</p>
          )}
        </div>
      ) : null}

      <h4>
        <Tip label={QUESTIONS.reliability} tip={QUESTIONS.reliabilityTip} />
      </h4>
      <ChoiceGroup
        value={value.reliability}
        onChange={chooseReliability}
        options={[
          {
            value: 'official',
            label: QUESTIONS.official,
            hint: QUESTIONS.officialHint,
            tip: QUESTIONS.officialTip,
          },
          {
            value: 'established',
            label: QUESTIONS.established,
            hint: QUESTIONS.establishedHint,
            tip: QUESTIONS.establishedTip,
          },
          {
            value: 'smaller',
            label: QUESTIONS.smaller,
            hint: QUESTIONS.smallerHint,
            tip: QUESTIONS.smallerTip,
          },
        ]}
      />
    </div>
  );
};

export default ScopeQuestions;
